import React, { useMemo, useState } from 'react';
import { escapeHtml } from '@/utils/htmlEscape';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ClientsTabHeader, type ClientsMode } from './ClientsTabHeader';
import { ClientsListMode } from './ClientsListMode';
import { ClientsBedCountMode } from './ClientsBedCountMode';
import RosterManagement from '@/components/RosterManagement';
import { ExcelImportDialog } from '@/components/ExcelImportDialog';
import NewClientForm from '@/components/NewClientForm';
import { useSites, type Site } from '@/hooks/useSites';
import { useClients } from '@/hooks/useClients';
import { useRooms } from '@/hooks/useRooms';
import { useDataExport } from '@/hooks/useDataExport';
import { useAuth } from '@/contexts/AuthContext';
import { isClientOnPass } from '@/utils/statusTransitions';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getDisplayStatus, type Client } from '@/types/shelter';

interface ClientsTabProps {
  onSelectClient?: (client: Client) => void;
  /** Force an initial mode (used for deep-linking from Staff dashboard). */
  initialMode?: ClientsMode;
}

export const ClientsTab: React.FC<ClientsTabProps> = ({ onSelectClient, initialMode = 'list' }) => {
  const { userRole, userSites } = useAuth();
  const { sites = [] } = useSites();
  const { clients, isLoading: clientsLoading, createClient } = useClients();
  const { rooms = [] } = useRooms();
  const { exportToCSV } = useDataExport();

  const isSuperAdmin = userRole === 'super_admin';

  // Scope sites by assignment (except super_admin which sees all)
  const assignedSites: Site[] = useMemo(() => {
    if (isSuperAdmin) return sites;
    if (!userSites || userSites.length === 0) return [];
    return sites.filter(s => userSites.includes(s.id));
  }, [sites, userSites, isSuperAdmin]);

  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(() => {
    if (isSuperAdmin) return sites[0]?.id ?? null;
    return userSites?.[0] ?? null;
  });

  // Keep selection valid as sites load
  React.useEffect(() => {
    if (!selectedSiteId && assignedSites.length > 0) {
      setSelectedSiteId(assignedSites[0].id);
    }
  }, [assignedSites, selectedSiteId]);

  const selectedSite = assignedSites.find(s => s.id === selectedSiteId) ?? null;
  const facilityName = selectedSite?.name ?? (assignedSites.length === 0 ? 'No Facility Assigned' : 'Select a Facility');

  const [mode, setMode] = useState<ClientsMode>(initialMode);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [rosterDialogOpen, setRosterDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);

  // Filter clients to the selected facility
  const facilityClients = useMemo(() => {
    if (!selectedSiteId) return [];
    return clients.filter(c => {
      const siteId = c.site_id || (c as any).siteId;
      return siteId === selectedSiteId;
    });
  }, [clients, selectedSiteId]);

  // Stat strip: bedded / capacity / on-pass / waitlist
  const stats = useMemo(() => {
    const facilityRooms = rooms.filter((r: any) => r.site_id === selectedSiteId);
    const capacity = facilityRooms.reduce((acc: number, r: any) => acc + (r.capacity || 1), 0);
    const bedded = facilityClients.filter(c => c.room_id).length;
    const available = Math.max(0, capacity - bedded);
    const onPass = facilityClients.filter(c => isClientOnPass(c)).length;
    const waitlist = facilityClients.filter(c => c.status === 'waitlist').length;
    const hospital = facilityClients.filter(c => c.status === 'hospital').length;

    return [
      { label: `/ ${capacity} bedded`, value: bedded, tone: 'primary' as const },
      { label: 'available', value: available, tone: 'success' as const },
      { label: 'on pass', value: onPass, tone: 'warning' as const },
      { label: 'hospital', value: hospital, tone: 'destructive' as const },
      { label: 'waitlist', value: waitlist, tone: 'muted' as const },
    ];
  }, [facilityClients, rooms, selectedSiteId]);

  // Overflow actions
  const handleExportCSV = () => {
    const rows = facilityClients.map(c => ({
      Name: c.name,
      Status: getDisplayStatus(c).status,
      Room: c.room || '—',
      'Case Manager': c.caseWorker || '—',
      Phone: c.phone || '',
      'Check-In': c.checkInTime ? format(new Date(c.checkInTime), 'yyyy-MM-dd HH:mm') : '',
    }));
    exportToCSV(rows, `clients_${facilityName.replace(/\s+/g, '_').toLowerCase()}`);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const rows = facilityClients
      .map((c) => `<tr><td>${escapeHtml(c.name || '')}</td><td>${escapeHtml(c.room || '—')}</td><td>${escapeHtml(getDisplayStatus(c).status)}</td><td>${escapeHtml(c.caseWorker || '—')}</td></tr>`)
      .join('');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Client Roster — ${escapeHtml(facilityName)}</title>
      <style>body{font-family:Arial,sans-serif;margin:30px;color:#000}h1{font-size:18pt;margin:0 0 4px}.header{text-align:center;border-bottom:2px solid #000;padding-bottom:12px;margin-bottom:20px}.date{font-size:10pt;color:#666}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{padding:8px 12px;border:1px solid #ddd;text-align:left}th{background:#f5f5f5;font-weight:600}.footer{margin-top:30px;text-align:center;font-size:8pt;color:#666;border-top:1px solid #ccc;padding-top:8px}@media print{button{display:none}}</style></head><body>
      <div class="header"><h1>Client Roster — ${escapeHtml(facilityName)}</h1><div class="date">Generated: ${new Date().toLocaleString()}</div></div>
      <table><thead><tr><th>Name</th><th>Room</th><th>Status</th><th>Case Manager</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="footer">Confidential — For Internal Use Only</div></body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  const handleSaveSnapshot = async () => {
    if (!selectedSiteId) {
      toast.error('Select a facility first');
      return;
    }
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const today = new Date();
      const dateStr = format(today, 'yyyy-MM-dd');
      const name = `Snapshot — ${format(today, 'MMM d, yyyy h:mm a')}`;

      const { data: roster, error } = await supabase
        .from('rosters')
        .insert({
          site_id: selectedSiteId,
          name,
          roster_date: dateStr,
          created_by: user.id,
        })
        .select()
        .single();
      if (error) throw error;

      const bedded = facilityClients.filter(c => c.room_id);
      if (bedded.length > 0) {
        const entries = bedded.map(c => ({
          roster_id: roster.id,
          client_id: c.id,
          room_id: c.room_id,
        }));
        const { error: entErr } = await supabase.from('roster_entries').insert(entries);
        if (entErr) throw entErr;
      }
      toast.success(`Snapshot saved (${bedded.length} clients)`);
    } catch (e: any) {
      toast.error(e.message || 'Failed to save snapshot');
    }
  };

  const canSignOff = ['super_admin', 'program_director', 'manager', 'supervisor'].includes(userRole ?? '');

  return (
    <div className="space-y-4">
      <ClientsTabHeader
        facilityName={facilityName}
        assignedSites={assignedSites}
        selectedSiteId={selectedSiteId}
        onSelectSiteId={setSelectedSiteId}
        isSuperAdmin={isSuperAdmin}
        mode={mode}
        onModeChange={setMode}
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        stats={stats}
        onNewIntake={() => setNewClientDialogOpen(true)}
        onManageRosters={() => setRosterDialogOpen(true)}
        onSaveSnapshot={handleSaveSnapshot}
        onImportExcel={() => setImportDialogOpen(true)}
        onExportCSV={handleExportCSV}
        onPrint={handlePrint}
      />

      {mode === 'list' ? (
        <ClientsListMode
          clients={facilityClients}
          isLoading={clientsLoading}
          search={search}
          statusFilter={statusFilter}
          onSelectClient={onSelectClient}
        />
      ) : (
        <ClientsBedCountMode
          siteId={selectedSiteId}
          siteName={selectedSite?.name}
          canSignOff={canSignOff}
        />
      )}

      {/* Manage Rosters dialog — preserved in full */}
      <Dialog open={rosterDialogOpen} onOpenChange={setRosterDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Rosters</DialogTitle>
          </DialogHeader>
          <RosterManagement />
        </DialogContent>
      </Dialog>

      {/* Excel import */}
      <ExcelImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        type="roster"
      />

      {/* New Intake */}
      <NewClientForm
        isOpen={newClientDialogOpen}
        onClose={() => setNewClientDialogOpen(false)}
        siteId={selectedSiteId || undefined}
        onSubmit={async (clientData: any) => {
          try {
            await createClient({
              ...clientData,
              siteId: selectedSiteId || undefined,
            });
            setNewClientDialogOpen(false);
          } catch (e) {
            // error toast handled by hook
          }
        }}
      />
    </div>
  );
};

export default ClientsTab;
